import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../login/logincss/Login.css";
import logo from "../../images/auravit_logo.png";
import { auth, db } from "../../firebase";
import { sendPasswordResetNotification } from "../../services/otpService";
import { collection, query, where, getDocs } from "firebase/firestore";
import { sendOtpEmail, sendOtpSms } from "../../services/otpService";
import { userService } from "../../services/firestoreService";
import {
  refreshStoredPasswordResetOtpChallenge,
  verifyStoredPasswordResetOtp,
  clearStoredPasswordResetOtpChallenge,
} from "../../services/authFlowService";

export default function Otp() {
  const location = useLocation();
  const navigate = useNavigate();

  // Retrieve email/phone passed from ForgotPassword page state
  const email = location.state?.email || "your email";
  const isPhone = location.state?.isPhone || false; 

  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [otpExpired, setOtpExpired] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // 60 seconds to match backend
  const [resendText, setResendText] = useState("Resend");
  const [isResending, setIsResending] = useState(false);
  const inputsRef = useRef([]);
  const [cachedUserEmail, setCachedUserEmail] = useState(null); // Cache phone lookup result

  // Animation state based on timer
  const getTimerState = () => {
    if (otpExpired) return "expired";
    if (timeLeft <= 5) return "critical";
    if (timeLeft <= 15) return "warning";
    return "normal";
  };

  const getCardClasses = () => {
    const baseClass = "forgot-card";
    const timerState = getTimerState();
    
    if (timerState === "expired") return `${baseClass} timer-expired`;
    if (timerState === "critical") return `${baseClass} timer-critical timer-active`;
    if (timeLeft > 0) return `${baseClass} timer-active`;
    return baseClass;
  };

  const getTimerClasses = () => {
    const timerState = getTimerState();
    return `timer timer-${timerState}`;
  };

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) {
      setOtpExpired(true);
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (sec) => {
    const minutes = Math.floor(sec / 60);
    const seconds = sec % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text").slice(0, OTP_LENGTH);
    if (/^\d+$/.test(pasteData)) {
      const pasteArray = pasteData.split("");
      setOtp(pasteArray);
      const lastFilled = pasteArray.length - 1;
      inputsRef.current[lastFilled].focus();
    }
    e.preventDefault();
  };

  const handleVerify = async () => {
    if (otp.some((d) => d === "")) {
      setError("Please enter all 6 digits.");
      // Reset OTP boxes when incomplete
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    setIsVerifying(true);
    try {
      const result = verifyStoredPasswordResetOtp(otp.join(""));
      if (!result.success) {
        setError(result.error);
        // Reset OTP boxes when incorrect
        setOtp(Array(OTP_LENGTH).fill(""));
        inputsRef.current[0]?.focus();
        return;
      }

      clearStoredPasswordResetOtpChallenge();
      
      let userEmail = email;
      
      // If in phone mode, get the user's email from their phone number - OPTIMIZED with caching
      if (isPhone) {
        // Use cached result if available
        if (cachedUserEmail) {
          userEmail = cachedUserEmail;
        } else {
          const phoneLast10 = email.replace("+63", "");
          
          // Query users by phone number
          const q = query(
            collection(db, 'users'),
            where('phone', '==', phoneLast10)
          );
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            const user = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
            userEmail = user.email || user.contactDetails?.email;
            setCachedUserEmail(userEmail); // Cache the result
          } else {
            // Fallback to contactDetails.contact field
            const q2 = query(
              collection(db, 'users'),
              where('contactDetails.contact', '==', phoneLast10)
            );
            const snapshot2 = await getDocs(q2);
            
            if (!snapshot2.empty) {
              const user = { id: snapshot2.docs[0].id, ...snapshot2.docs[0].data() };
              userEmail = user.email || user.contactDetails?.email;
              setCachedUserEmail(userEmail); // Cache the result
            } else {
              throw new Error("User not found with this phone number");
            }
          }
        }
      }
      
      await sendPasswordResetNotification({ email: userEmail });
      navigate("/reset-password", {
        state: {
          email: userEmail,
          fromForgotPassword: true,
        },
      });
    } catch (err) {
      console.error("Verification error:", err);
      setError(err?.message || "Failed to verify OTP. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      const refreshed = refreshStoredPasswordResetOtpChallenge();
      if (isPhone) {
        await sendOtpSms({
          phone: refreshed.email,
          otp: refreshed.otp,
          purpose: "forgot-password",
          requestId: refreshed.requestId,
          expiresAt: refreshed.expiresAt,
        });
      } else {
        await sendOtpEmail({
          email: refreshed.email,
          otp: refreshed.otp,
          purpose: "forgot-password",
          requestId: refreshed.requestId,
          expiresAt: refreshed.expiresAt,
        });
      }
      setOtp(Array(OTP_LENGTH).fill(""));
      setError("");
      setOtpExpired(false);
      setTimeLeft(60);
      inputsRef.current[0]?.focus();
      setResendText("Sent!");
      setTimeout(() => setResendText("Resend"), 3000);
    } catch (err) {
      console.error("Resend failed:", err);
      setError(err?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  // Handle cancel button
  const handleCancel = () => {
    clearStoredPasswordResetOtpChallenge();
    navigate("/forgotpass");
  };

  return (
    <>
    <div className="verification-logo-container">
      <img
        src={logo}
        alt="AuraVit"
        className="verification-logo"
        onClick={() => navigate("/")}
      />
    </div>
    <div className="verification-container">
      <div className="verification-card">
        <h1 className="verification-title">Enter Verification Code</h1>
        <p className="verification-subtitle">
          Enter 6-digit code we sent to your registered {isPhone ? "contact number" : "email"}: <strong>{email}</strong>
        </p>

        <div className={`verification-otp-inputs ${error ? "shake" : ""}`}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              className={`verification-otp-input ${error && !digit ? "error" : ""}`}
              disabled={otpExpired}
            />
          ))}
        </div>

        {error && <div className="verification-error-text">{error}</div>}

        <p className="verification-timer-section">
          Code expires in <span className={`verification-timer ${otpExpired ? "expired" : ""}`}>{formatTime(timeLeft)}</span>
        </p>

        <p className="verification-resend-section">
          Didn't receive a code?{" "}
          <span className={`verification-resend-link ${isResending ? "disabled" : ""}`} onClick={handleResend} style={{ pointerEvents: isResending ? "none" : "auto", opacity: isResending ? 0.5 : 1 }}>{resendText}</span>
        </p>

        <button className="verification-verify-btn" onClick={handleVerify} disabled={isVerifying || otpExpired || otp.includes("")}>
          {isVerifying ? "Verifying..." : "Verify"}
        </button>

        <button
          className="verification-cancel-btn"
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>
    </div>
    </>
  );
}
